<script setup lang="ts">
import { NuxtLink } from '#components'
import { EvmHomeBlockInfo } from '~/api/repository'

interface IProps {
  block: EvmHomeBlockInfo
  height: number
  delay: number
  playAnimation: boolean  
  isLatest: boolean
}

const heightTime = 0.3
const heightDuration = computed(() => `${heightTime}s`)

const scaleDelay = computed(() => `${heightTime + props.delay}s`)

const props = defineProps<IProps>()

const blockHeight = computed(() => `${props.height}%`)
</script>

<template>
  <div
    class="block-container"
    :class="{latest: isLatest}"
  >
    <div class="dot-container">
      <div
        v-for="index in 3"
        :key="index"
        class="dot"
      />
    </div>
    <NuxtLink
      :to="block.BNO && routes.getBlockDetailPageWithTab(block.BNO)"
      class="block-box"
      :class="{animate: playAnimation}"
      v-tooltip="{content:`<div class='text-center'>${block.BNO}<br /><span class='text-sm'>(txn: ${block.TRXCNT})</span></div>`, html: true}"
    >
      <div class="block-height" />
    </NuxtLink>
    <div class="dot-container front">
      <div
        v-for="index in 3"
        :key="index"
        class="dot"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.block-container {
  @apply flex justify-center items-center px-px w-full;

  .dot-container {
    @apply flex justify-around w-full;

    .dot {
      @apply bg-white/40 rounded;
      width: 0.15rem;
      height: 0.15rem;
    }
  }

  .block-box {
    @apply shrink-0 overflow-hidden relative w-12 h-12 rounded-lg bg-card-background/10 hover:shadow-[0_4px_16px]
    hover:shadow-point-color/20 focus:shadow-[0_4px_16px] focus:shadow-point-color/20;

    &.animate {
      animation: {
        duration: 2s;
        name: changeScale;
        timing-function: linear;
        delay: v-bind(scaleDelay);
      };
      @keyframes changeScale {
        from {
          transform: scale(1.0);
        }
        5% {
          transform: scale(1.2);
        }
        10% {
          transform: scale(1.0);
        }
      }
    }

    .block-height {
      @apply absolute bottom-0 left-0 w-full bg-card-background/40;
      height: v-bind(blockHeight);
    }

    &:hover {
      .block-height {
        @apply bg-point-color;
      }
    }
  }

  &.latest {
    .dot-container.front {
      opacity: 0;
    }

    .block-box {
      @apply shadow-[0_4px_16px] shadow-point-color/20 bg-point-color/10 border border-primary/20;
      animation: none;

      .block-height {
        @apply bg-point-color;
        animation: changeHeight v-bind(heightDuration) linear;
        @keyframes changeHeight {
          from {
            height: 0;
          }
          to {
            height: v-bind(blockHeight);
          }
        }
      }
    }
  }
}
</style>